<template>
  <div class="rich-text-editor">
    <editor-content :editor="editor" class="editor-content" />
    <div v-if="showToolbar && editor" class="editor-toolbar">
      <button
        @click="editor.chain().focus().toggleBold().run()"
        :class="{ 'is-active': editor.isActive('bold') }"
      >
        Bold
      </button>
      <button
        @click="editor.chain().focus().toggleItalic().run()"
        :class="{ 'is-active': editor.isActive('italic') }"
      >
        Italic
      </button>
      <button
        @click="editor.chain().focus().toggleBulletList().run()"
        :class="{ 'is-active': editor.isActive('bulletList') }"
      >
        Bullet List
      </button>
      <button
        @click="editor.chain().focus().toggleTaskList().run()"
        :class="{ 'is-active': editor.isActive('taskList') }"
      >
        Task List
      </button>
      <button
        @click="insertMention"
        class="mention-button"
        title="Mention someone (@)"
      >
        @Mention
      </button>
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import Mention from '@tiptap/extension-mention';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Placeholder from '@tiptap/extension-placeholder';
import { suggestion } from './mention-suggestion';

export default {
  name: 'RichTextEditor',
  components: {
    EditorContent,
  },
  props: {
    modelValue: {
      type: Object,
      default: () => ({ type: 'doc', content: [{ type: 'paragraph' }] }),
    },
    placeholder: {
      type: String,
      default: 'Write your note here...',
    },
    showToolbar: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update:modelValue', 'mention-clicked'],
  data() {
    return {
      editor: null,
      lastKeyPressed: null,
    };
  },
  watch: {
    modelValue: {
      deep: true,
      handler(value) {
        // Parse string content if needed
        const content = typeof value === 'string' ? JSON.parse(value) : value;
        
        // Only update if the editor exists and content differs
        const editorContent = this.editor?.getJSON();
        if (this.editor && JSON.stringify(content) !== JSON.stringify(editorContent)) {
          this.editor.commands.setContent(content, false);
        }
      }
    }
  },
  mounted() {
    // Parse initial content if it's a string
    const initialContent = typeof this.modelValue === 'string' ? 
      JSON.parse(this.modelValue) : 
      this.modelValue;

    this.editor = new Editor({
      extensions: [
        StarterKit,
        Placeholder.configure({
          placeholder: this.placeholder,
        }),
        TaskList,
        TaskItem.configure({
          nested: true,
        }),
        Mention.configure({
          HTMLAttributes: {
            class: 'mention',
          },
          suggestion,
          renderLabel({ node }) {
            return `@${node.attrs.label ?? node.attrs.id}`;
          }
        }),
      ],
      content: initialContent,
      onUpdate: ({ editor }) => {
        this.$emit('update:modelValue', editor.getJSON());
      },
      editorProps: {
        handleKeyDown: (view, event) => {
          // Store the last key pressed to help with mention triggering
          this.lastKeyPressed = event.key;
        }
      }
    });
  },
  beforeUnmount() {
    if (this.editor) {
      this.editor.destroy();
    }
  },
  methods: {
    insertMention() {
      // Insert @ character and trigger mention suggestions
      this.editor
        .chain()
        .focus()
        .insertContent('@')
        .run();

      // Simulate typing @ to trigger suggestions
      const view = this.editor.view;
      const event = new KeyboardEvent('keydown', {
        key: '@',
        keyCode: 50,
        code: 'Digit2',
        shiftKey: true,
        bubbles: true
      });
      view.dom.dispatchEvent(event);
    }
  }
};
</script>

<style scoped>
.rich-text-editor {
  border: 1px solid #ddd;
  border-radius: 4px;
}

.editor-content {
  padding: 1rem;
  min-height: 150px;
  max-height: 500px;
  overflow-y: auto;
}

.editor-toolbar {
  border-top: 1px solid #ddd;
  padding: 0.5rem;
  display: flex;
  gap: 0.5rem;
  background: #f8f9fa;
}

.editor-toolbar button {
  padding: 0.25rem 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
}

.editor-toolbar button:hover {
  background: #f0f0f0;
}

.editor-toolbar button.is-active {
  background: #e9ecef;
  border-color: #ced4da;
}

.mention-button {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: #666;
}

.mention-button:hover {
  color: #2196f3;
}

:deep(.mention) {
  color: #0366d6;
  background: #f1f8ff;
  border-radius: 4px;
  padding: 0.1rem 0.3rem;
  text-decoration: none;
  cursor: pointer;
}

:deep(.mention:hover) {
  background: #def;
}

:deep(.ProseMirror) {
  outline: none;
  text-align: left;
}

:deep(.ProseMirror p) {
  margin: 0.5rem 0;
  text-align: left;
}

:deep(.ProseMirror p.is-editor-empty:first-child::before) {
  color: #adb5bd;
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
}

:deep(.task-list) {
  list-style: none;
  padding-left: 0;
  margin: 0.5rem 0;
}

:deep(.task-list-item) {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin: 0.25rem 0;
  padding-left: 0;
}

:deep(.task-list-item input[type="checkbox"]) {
  margin: 0;
  cursor: pointer;
}

:deep(.task-list-item > div) {
  flex: 1;
  margin: 0;
  text-align: left;
}

:deep(ul[data-type="taskList"]) {
  list-style: none;
  padding: 0;
}

:deep(ul[data-type="taskList"] li) {
  display: flex;
  gap: 0.5rem;
  margin: 0.5rem 0;
  align-items: center;
}

:deep(ul[data-type="taskList"] li > label) {
  flex: 0 0 auto;
  margin: 0;
  user-select: none;
}

:deep(ul[data-type="taskList"] li > div) {
  flex: 1;
  margin: 0;
  text-align: left;
}

:deep(ul[data-type="bulletList"]) {
  padding-left: 1.2rem;
  margin: 0.5rem 0;
}

:deep(ul[data-type="bulletList"] li) {
  margin: 0.25rem 0;
}
</style>
